import { call, put, takeLatest } from 'redux-saga/effects'

import {
  AxiosServerResponse,
  CUSTOMER_REGISTRY_ROUTES,
  LOGIN_ROUTES,
  Server,
  ServerUNC,
  serverStructureFactory,
  SERVER_HEADERS,
} from 'src/services'
import { LOCAL_STORAGE_KEYS } from 'src/config'
import { showError, showServerError } from 'src/store/ducks/error'
import { addLoading, removeLoading } from 'src/store/ducks/loading'

import { AUTH_TYPES, AuthData, RequestAuthenticateAction } from './types'
import { setAuthenticated } from './actions'

type RequestData = Pick<
  UncDashboard.User & UncDashboard.Login,
  'usuario_emaillogin' | 'usuario_senha' | 'login_macadress'
>

type CustomerRegistry = {
  sistema_codigo: string
  cliente_codigo: string
  clienteregistro_hostws?: string
  clienteregistro_versao?: string
}

type CustomerRegistryResponse = AxiosServerResponse<CustomerRegistry>

export function* requestAuthenticateWatcher() {
  yield takeLatest(AUTH_TYPES.REQUEST_AUTHENTICATE, handleAuthentication)
}

export function* handleFetchCustomerId(email: string) {
  const { data }: CustomerRegistryResponse = yield call(
    ServerUNC.get,
    [CUSTOMER_REGISTRY_ROUTES.CONSULTA_EMAIL_CLIENTE, email].join('/'),
    {
      headers: {
        [SERVER_HEADERS.PUBCLIENTEREGISTRO]: '',
      },
    },
  )

  if (data.isSuccessful && data.hasData) {
    const services: CustomerRegistry[] = (data as any).rows || [
      data.getFirstData(),
    ]

    const dashboardService = services.find((s) => s.sistema_codigo === '0001')

    if (!dashboardService) {
      yield put(
        showError([
          'Atenção! Você não possui o serviço de Dashboard ou ele está desativado.',
        ]),
      )
      return undefined
    }

    Server.defaults.auth = {
      username: process.env.REACT_APP_API_USER || 'MASTER',
      password: process.env.REACT_APP_API_PASSWORD || 'MASTER',
    }

    if (
      process.env.NODE_ENV === 'production' &&
      dashboardService.clienteregistro_hostws
    ) {
      Server.defaults.baseURL = dashboardService.clienteregistro_hostws.replace(
        /\\\//g,
        '/',
      )
    } else {
      Server.defaults.baseURL = process.env.REACT_APP_API_URL
    }

    yield call(
      [localStorage, localStorage.setItem],
      LOCAL_STORAGE_KEYS.CUSTOMER_REGISTRY,
      JSON.stringify(dashboardService),
    )

    return dashboardService.cliente_codigo
  }

  if (data.messages?.length) {
    yield put(showServerError(data.messages))
  } else {
    yield put(showError([]))
  }
}

export function* handleUserAuthRequest(
  { email, password, onAuthenticated }: RequestAuthenticateAction['payload'],
  customerId: string,
) {
  const requestData: RequestData = {
    usuario_emaillogin: email,
    usuario_senha: password,
    login_macadress: '-',
  }

  const { data }: AxiosServerResponse<AuthData> = yield call(
    Server.put,
    LOGIN_ROUTES.LOGIN_EMAIL,
    serverStructureFactory(requestData),
    {
      headers: {
        [SERVER_HEADERS.PUBCLIENTEREGISTRO]: customerId,
      },
    },
  )

  if (data.isSuccessful) {
    const authData = data.getFirstData()

    if (authData) {
      const authDataWithCustomerId: AuthData = {
        ...authData,
        cliente_codigo: customerId,
      }

      yield call(
        [localStorage, localStorage.setItem],
        LOCAL_STORAGE_KEYS.AUTH_DATA,
        JSON.stringify(authDataWithCustomerId),
      )

      yield put(setAuthenticated(authDataWithCustomerId))
      if (onAuthenticated) yield call(onAuthenticated)
    }
  } else if (data.messages.length) {
    yield put(showServerError(data.messages))
  } else {
    yield put(showError([]))
  }
}

export function* handleAuthentication(action: RequestAuthenticateAction) {
  try {
    yield put(addLoading(AUTH_TYPES.REQUEST_AUTHENTICATE))

    const customerId: string | undefined = yield call(
      handleFetchCustomerId,
      action.payload.email,
    )

    if (customerId) {
      yield call(handleUserAuthRequest, action.payload, customerId)
    }
  } catch (e: any) {
    if (e.message === 'Network Error') {
      const failedHost = e?.config?.baseURL || 'Servidor desconhecido'

      yield put(showError([`Erro de conexão (timeout)\n${failedHost}`]))
    } else if (e instanceof Error) {
      yield put(showError(e.message ? [e.message] : []))
    } else {
      yield put(showError(['An unknown error occurred.']))
    }
  } finally {
    yield put(removeLoading(AUTH_TYPES.REQUEST_AUTHENTICATE))
  }
}
